import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { InMemoryDataService } from '../../core/data/in-memory-data.service';
import { OrganisationFeatureFlags, UserContextService } from '../../core/auth/user-context.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';

type ContextRole = 'admin' | 'student' | 'instructor' | null;

@Component({
  selector: 'app-context-entry-page',
  standalone: true,
  imports: [MatCardModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatSelectModule, MatSlideToggleModule, TranslatePipe],
  template: `
    <h1>{{ 'context.choose' | t }}</h1>

    <mat-card class="org-context-card">
      <mat-card-content>
        <div class="role-title"><mat-icon>business</mat-icon> {{ 'context.organisationContext' | t }}</div>
        <mat-form-field>
          <mat-label>{{ 'context.organisation' | t }}</mat-label>
          <mat-select [value]="selectedOrganisationId()" (valueChange)="changeOrganisation($event)">
            @for (item of organisations(); track item.id) {
              <mat-option [value]="item.id">{{ item.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </mat-card-content>
    </mat-card>

    <div class="role-grid">
      <mat-card class="role-card" [class.selected]="selectedRole() === 'admin'">
        <mat-card-content>
          <div class="role-title"><mat-icon>admin_panel_settings</mat-icon> {{ 'context.rootApp' | t }}</div>
          <p>{{ 'context.rootDescription' | t }}</p>
          <button mat-flat-button color="primary" (click)="enterRootContext()">{{ 'context.openRootApp' | t }}</button>
        </mat-card-content>
      </mat-card>

      @if (features().studentApp) {
      <mat-card class="role-card" [class.selected]="selectedRole() === 'student'">
        <mat-card-content>
          <div class="role-title"><mat-icon>school</mat-icon> {{ 'context.studentApp' | t }}</div>
          <p>{{ 'context.studentDescription' | t }}</p>
          <button mat-stroked-button (click)="selectRole('student')">{{ 'context.selectStudentContext' | t }}</button>
          @if (selectedRole() === 'student') {
            <div class="inline-chooser">
              <mat-form-field>
                <mat-label>{{ 'context.student' | t }}</mat-label>
                <mat-select [value]="selectedStudentId()" (valueChange)="selectedStudentId.set($event)">
                  @for (student of organisationStudents(); track student.id) {
                    <mat-option [value]="student.id">{{ student.name }} {{ student.surname }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-flat-button color="primary" [disabled]="!selectedStudentId()" (click)="enterStudentContext()">
                {{ 'context.enterStudentApp' | t }}
              </button>
            </div>
          }
        </mat-card-content>
      </mat-card>
      }

      @if (features().instructorApp) {
      <mat-card class="role-card" [class.selected]="selectedRole() === 'instructor'">
        <mat-card-content>
          <div class="role-title"><mat-icon>badge</mat-icon> {{ 'context.instructorApp' | t }}</div>
          <p>{{ 'context.instructorDescription' | t }}</p>
          <button mat-stroked-button (click)="selectRole('instructor')">{{ 'context.selectInstructorContext' | t }}</button>
          @if (selectedRole() === 'instructor') {
            <div class="inline-chooser">
              <mat-form-field>
                <mat-label>{{ 'context.instructor' | t }}</mat-label>
                <mat-select [value]="selectedInstructorId()" (valueChange)="selectedInstructorId.set($event)">
                  @for (instructor of organisationInstructors(); track instructor.id) {
                    <mat-option [value]="instructor.id">{{ instructor.name }} {{ instructor.surname }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button
                mat-flat-button
                color="primary"
                [disabled]="!selectedInstructorId()"
                (click)="enterInstructorContext()"
              >
                {{ 'context.enterInstructorApp' | t }}
              </button>
            </div>
          }
        </mat-card-content>
      </mat-card>
      }

      @if (features().backofficeApp) {
      <mat-card class="role-card">
        <mat-card-content>
          <div class="role-title"><mat-icon>admin_panel_settings</mat-icon> {{ 'context.backofficeApp' | t }}</div>
          <p>{{ 'context.backofficeDescription' | t }}</p>
          <div class="backoffice-actions">
            <button mat-flat-button color="primary" (click)="openBackoffice()">{{ 'context.openBackoffice' | t }}</button>
            @if (features().userManagement) {
              <button mat-stroked-button (click)="openUserManagement()">{{ 'context.openUserManagement' | t }}</button>
            }
          </div>
        </mat-card-content>
      </mat-card>
      }
    </div>

    <mat-card class="feature-config-card">
      <mat-card-content>
        <div class="role-title"><mat-icon>tune</mat-icon> {{ 'context.planConfig.title' | t }}</div>
        <p>{{ 'context.planConfig.description' | t }}</p>
        <div class="feature-grid">
          <mat-slide-toggle
            [checked]="features().instructorPayoutProjection"
            (change)="setFeature('instructorPayoutProjection', $event.checked)"
          >
            {{ 'context.planConfig.instructorPayoutProjection' | t }}
          </mat-slide-toggle>
          <mat-slide-toggle [checked]="features().lessonPlanning" (change)="setFeature('lessonPlanning', $event.checked)">
            {{ 'context.planConfig.lessonPlanning' | t }}
          </mat-slide-toggle>
          <mat-slide-toggle [checked]="features().userManagement" (change)="setFeature('userManagement', $event.checked)">
            {{ 'context.planConfig.userManagement' | t }}
          </mat-slide-toggle>
        </div>
      </mat-card-content>
    </mat-card>

    @if (features().demoDataTools) {
    <mat-card class="demo-data-card">
      <mat-card-content>
        <div class="role-title"><mat-icon>science</mat-icon> {{ 'context.demoData.title' | t }}</div>
        <p>{{ 'context.demoData.description' | t }}</p>
        <div class="demo-data-actions">
          <button mat-stroked-button color="warn" (click)="simulateCleanApp()">
            {{ 'context.demoData.cleanAction' | t }}
          </button>
          <button mat-button (click)="restoreDemoData()">{{ 'context.demoData.restoreAction' | t }}</button>
        </div>
      </mat-card-content>
    </mat-card>
    }

  `,
  styles: `
    :host {
      display: block;
    }
    h1 {
      margin: 0 0 14px;
    }
    .org-context-card {
      margin-bottom: 14px;
      border: 1px solid #d7e1f5;
      max-width: 520px;
    }
    .role-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
    }
    .role-card {
      border: 1px solid #d7e1f5;
    }
    .role-card.selected {
      border-color: #2d62ff;
      box-shadow: 0 0 0 1px #2d62ff inset;
    }
    .role-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      margin-bottom: 8px;
    }
    .inline-chooser {
      margin-top: 12px;
      padding-top: 10px;
      border-top: 1px dashed #d7e1f5;
      display: grid;
      gap: 8px;
    }
    .demo-data-card {
      margin-top: 14px;
      border: 1px dashed #d7e1f5;
      max-width: 980px;
    }
    .feature-config-card {
      margin-top: 14px;
      border: 1px solid #d7e1f5;
      max-width: 980px;
    }
    .feature-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;
    }
    .backoffice-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .demo-data-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }
    mat-form-field {
      width: 100%;
      margin-bottom: 8px;
    }
    @media (max-width: 980px) {
      .role-grid {
        grid-template-columns: 1fr;
      }
    }
  `
})
export class ContextEntryPageComponent {
  private readonly dataService = inject(InMemoryDataService);
  private readonly userContext = inject(UserContextService);
  private readonly router = inject(Router);

  readonly students = this.dataService.students;
  readonly instructors = this.dataService.instructors;
  readonly organisations = this.dataService.organisations;
  readonly selectedRole = signal<ContextRole>(null);
  readonly selectedOrganisationId = signal<number>(this.userContext.currentOrganisationId());
  readonly selectedStudentId = signal<number | null>(null);
  readonly selectedInstructorId = signal<number | null>(null);
  readonly features = this.userContext.currentOrganisationFeatures;
  readonly organisationStudents = computed(() =>
    this.students().filter((item) => item.organisationId === this.selectedOrganisationId())
  );
  readonly organisationInstructors = computed(() =>
    this.instructors().filter((item) => item.organisationId === this.selectedOrganisationId())
  );
  readonly hasStudentData = computed(() => this.organisationStudents().length > 0);
  readonly hasInstructorData = computed(() => this.organisationInstructors().length > 0);

  changeOrganisation(organisationId: number): void {
    this.selectedOrganisationId.set(organisationId);
    this.userContext.setContextOrganisation(organisationId);
    this.selectedRole.set(null);
    this.selectedStudentId.set(null);
    this.selectedInstructorId.set(null);
  }

  selectRole(role: Exclude<ContextRole, null>): void {
    this.selectedRole.set(role);
    if (role === 'student' && !this.selectedStudentId() && this.hasStudentData()) {
      this.selectedStudentId.set(this.organisationStudents()[0].id);
    }
    if (role === 'instructor' && !this.selectedInstructorId() && this.hasInstructorData()) {
      this.selectedInstructorId.set(this.organisationInstructors()[0].id);
    }
  }

  enterStudentContext(): void {
    const id = this.selectedStudentId();
    if (!id) {
      return;
    }
    this.userContext.setStudentContext(id);
    this.router.navigate(['/student']);
  }

  enterInstructorContext(): void {
    const id = this.selectedInstructorId();
    if (!id) {
      return;
    }
    this.userContext.setInstructorContext(id);
    this.router.navigate(['/instructor']);
  }

  openBackoffice(): void {
    if (!this.features().backofficeApp) {
      return;
    }
    this.userContext.resetToBackoffice();
    this.router.navigate(['/dashboard']);
  }

  enterRootContext(): void {
    this.userContext.setRootContext();
    this.router.navigate(['/admin']);
  }

  openUserManagement(): void {
    if (!this.features().userManagement) {
      return;
    }
    this.userContext.resetToBackoffice();
    this.router.navigate(['/admin/users']);
  }

  simulateCleanApp(): void {
    this.dataService.clearDemoDataKeepCategories();
    this.userContext.resetToBackoffice();
    this.selectedRole.set(null);
    this.selectedStudentId.set(null);
    this.selectedInstructorId.set(null);
    this.router.navigate(['/context']);
  }

  restoreDemoData(): void {
    this.dataService.resetDemoData();
    this.userContext.resetToBackoffice();
    this.selectedRole.set(null);
    this.selectedStudentId.set(null);
    this.selectedInstructorId.set(null);
    this.router.navigate(['/context']);
  }

  setFeature(
    feature: keyof Pick<OrganisationFeatureFlags, 'instructorPayoutProjection' | 'lessonPlanning' | 'userManagement'>,
    enabled: boolean
  ): void {
    this.userContext.setOrganisationFeature(this.selectedOrganisationId(), feature, enabled);
  }
}
